'use client';

/**
 * Error page of the console (docs/ERRORES.md): an unexpected failure while
 * rendering a page shows ONLY a generic text and an opaque reference
 * (`ARG-` + 8 digits) derived from the Next.js digest — the same one the
 * server logged with its internal code (src/instrumentation.ts). Never the
 * error message, a stack trace, a code or a cause. Errors of the browser
 * itself have no digest (nothing was logged): generic text only.
 *
 * Texts inline (es, ca, en, fr): the error boundary cannot load the message
 * files of the server; the language is the one of the page (`<html lang>`).
 */
import { refFromDigest } from '@argos/shared/errors';
import { useSyncExternalStore } from 'react';

const TEXTS = {
  es: {
    title: 'Algo ha fallado',
    body: 'Se ha producido un error interno ({ref}). Si persiste, contacta con soporte indicando este número.',
    bodyNoRef: 'Se ha producido un error interno. Si persiste, contacta con soporte.',
    retry: 'Reintentar',
    home: 'Ir al inicio',
  },
  ca: {
    title: 'Alguna cosa ha fallat',
    body: "S'ha produït un error intern ({ref}). Si persisteix, contacta amb el suport indicant aquest número.",
    bodyNoRef: "S'ha produït un error intern. Si persisteix, contacta amb el suport.",
    retry: 'Torna-ho a provar',
    home: "Ves a l'inici",
  },
  en: {
    title: 'Something went wrong',
    body: 'An internal error occurred ({ref}). If it persists, contact support quoting this number.',
    bodyNoRef: 'An internal error occurred. If it persists, contact support.',
    retry: 'Try again',
    home: 'Go to the home page',
  },
  fr: {
    title: 'Un problème est survenu',
    body: "Une erreur interne s'est produite ({ref}). Si le problème persiste, contactez le support en indiquant ce numéro.",
    bodyNoRef: "Une erreur interne s'est produite. Si le problème persiste, contactez le support.",
    retry: 'Réessayer',
    home: "Aller à l'accueil",
  },
} as const;
type Locale = keyof typeof TEXTS;

const isLocale = (value: string): value is Locale => Object.hasOwn(TEXTS, value);
const noSubscription = () => () => undefined;
/** Language of the page (`<html lang>`); Spanish on the server and by default. */
const pageLocale = (): Locale => {
  const lang = document.documentElement.lang.slice(0, 2).toLowerCase();
  return isLocale(lang) ? lang : 'es';
};

export default function ErrorPage({
  error,
  retry,
}: {
  error: Error & { digest?: string };
  retry: () => void;
}) {
  const locale = useSyncExternalStore(noSubscription, pageLocale, () => 'es' as const);
  const t = TEXTS[locale];
  const ref =
    typeof error.digest === 'string' && error.digest !== '' ? refFromDigest(error.digest) : null;
  return (
    <main className="mx-auto flex w-full max-w-xl flex-col gap-4 px-4 py-16">
      <h1 className="font-title text-2xl font-bold text-fg">{t.title}</h1>
      <p role="alert" className="text-muted">
        {ref !== null ? t.body.replace('{ref}', ref) : t.bodyNoRef}
      </p>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => {
            retry();
          }}
          className="rounded-xl bg-primary-strong px-4 py-2 text-sm font-semibold text-white"
        >
          {t.retry}
        </button>
        {/* A full navigation (not a client transition) leaves the failed state. */}
        <a
          href="/"
          className="rounded-xl border border-border-strong px-4 py-2 text-sm font-semibold text-fg"
        >
          {t.home}
        </a>
      </div>
    </main>
  );
}
